import type { Graphics } from 'pixi.js';

/**
 * 1.22 바닥 경고 — 적이 치기 전에 맞을 자리를 붉게 보여 준다.
 * 바깥 테두리는 처음부터 다 보이고, 안쪽 채움이 가운데서부터 차오른다(u 0 → 1) — 다 차면 친다.
 * Graphics.arc 는 쓰지 않는다(시작점을 안 찍으면 원점에서 선이 날아온다 — 1.21 광풍 버그). 전부 점을 찍어 긋는다.
 */

/** 이번 스텝의 경고 자리 — 자동 조종(dev/sim.ts)이 사람처럼 경고를 보고 비키게. 스텝마다 비운다 */
export interface Hazard {
  kind: 'circle' | 'band' | 'sector' | 'ring';
  x: number;
  y: number;
  r: number;
  dir: number;
  len: number;
  hw: number;
  half: number;
  gaps: number[];
}
export const hazards: Hazard[] = [];

function note(h: Partial<Hazard> & Pick<Hazard, 'kind' | 'x' | 'y'>): void {
  if (hazards.length < 64) hazards.push({ r: 0, dir: 0, len: 0, hw: 0, half: 0, gaps: [], ...h });
}

const RED = 0xff3b2f;
const HOT = 0xffd0a0;

function ringPts(cx: number, cy: number, r: number, a0: number, a1: number, n: number, sy = 1): number[] {
  const pts: number[] = [];
  for (let k = 0; k <= n; k++) {
    const a = a0 + ((a1 - a0) * k) / n;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r * sy);
  }
  return pts;
}

/** 원 — 보스 둘레 휘두르기 · 내려찍기 자리 · 불기둥 */
export function warnCircle(g: Graphics, x: number, y: number, r: number, u: number, color = RED): void {
  const n = Math.max(16, Math.min(56, Math.round(r / 5)));
  const k = Math.max(0, Math.min(1, u));
  note({ kind: 'circle', x, y, r });
  g.poly(ringPts(x, y, r, 0, Math.PI * 2, n), true).fill({ color, alpha: 0.1 + 0.08 * k });
  if (k > 0.02) g.poly(ringPts(x, y, r * k, 0, Math.PI * 2, n), true).fill({ color, alpha: 0.16 + 0.14 * k });
  g.poly(ringPts(x, y, r, 0, Math.PI * 2, n), true).stroke({ width: 2, color: k > 0.85 ? HOT : color, alpha: 0.55 + 0.4 * k });
}

/** 부채꼴 — 할퀴기 · 화염 초승달. dir = 가운데 방향(rad), half = 반각(rad) */
export function warnSector(g: Graphics, x: number, y: number, r: number, dir: number, half: number, u: number, color = RED): void {
  const n = Math.max(10, Math.round((half * 2 * r) / 14));
  const k = Math.max(0, Math.min(1, u));
  note({ kind: 'sector', x, y, r, dir, half });
  const shape = (rr: number): number[] => [x, y, ...ringPts(x, y, rr, dir - half, dir + half, n)];
  g.poly(shape(r), true).fill({ color, alpha: 0.1 + 0.08 * k });
  if (k > 0.02) g.poly(shape(r * k), true).fill({ color, alpha: 0.16 + 0.14 * k });
  g.poly(shape(r), true).stroke({ width: 2, color: k > 0.85 ? HOT : color, alpha: 0.55 + 0.4 * k });
}

/** 띠 — 찌르기 · 돌진 · 이무기. (x, y)에서 dir 쪽으로 len, 반폭 hw. 채움은 시작점에서 끝 쪽으로 차오른다 */
export function warnBand(g: Graphics, x: number, y: number, dir: number, len: number, hw: number, u: number, color = RED): void {
  const k = Math.max(0, Math.min(1, u));
  const cx = Math.cos(dir);
  const cy = Math.sin(dir);
  const nx = -cy * hw;
  const ny = cx * hw;
  note({ kind: 'band', x, y, dir, len, hw });
  const rect = (l: number): number[] => [x + nx, y + ny, x + cx * l + nx, y + cy * l + ny, x + cx * l - nx, y + cy * l - ny, x - nx, y - ny];
  g.poly(rect(len), true).fill({ color, alpha: 0.1 + 0.08 * k });
  if (k > 0.02) g.poly(rect(len * k), true).fill({ color, alpha: 0.16 + 0.14 * k });
  g.poly(rect(len), true).stroke({ width: 2, color: k > 0.85 ? HOT : color, alpha: 0.55 + 0.4 * k });
}

/** 가는 점선 — 돌진할 쪽 (일반 적, 가볍게) */
export function warnDash(g: Graphics, x: number, y: number, dir: number, len: number, u: number, color = RED): void {
  const cx = Math.cos(dir);
  const cy = Math.sin(dir);
  const step = 14;
  const alpha = 0.25 + 0.5 * Math.max(0, Math.min(1, u));
  for (let d = 10; d < len; d += step) {
    const x0 = x + cx * d;
    const y0 = y + cy * d;
    g.moveTo(x0, y0).lineTo(x0 + cx * 7, y0 + cy * 7);
  }
  g.stroke({ width: 2, color, alpha });
}

/** 가락지 — 홍련나찰 회전무의 칼 고리처럼 퍼지는 띠. gaps: 빈틈 가운데 각(rad) 들, gapHalf: 빈틈 반각 */
export function warnRing(g: Graphics, x: number, y: number, r: number, width: number, gaps: number[], gapHalf: number, alpha: number, color = RED): void {
  const segs = 72;
  note({ kind: 'ring', x, y, r, hw: width, half: gapHalf, gaps: gaps.slice() });
  const inGap = (a: number): boolean => gaps.some((c) => Math.abs(Math.atan2(Math.sin(a - c), Math.cos(a - c))) < gapHalf);
  let run: number[] = [];
  const flush = (): void => {
    if (run.length >= 4) g.poly(run, false).stroke({ width, color, alpha });
    run = [];
  };
  for (let k = 0; k <= segs; k++) {
    const a = (k / segs) * Math.PI * 2;
    if (inGap(a)) {
      flush();
      continue;
    }
    run.push(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  flush();
}
